import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import * as registrationApi from '../api/registration';
import { useToast } from '../components/toast';
import { ErrorAlert, Field, Spinner } from '../components/ui';
import { ui, cn } from '../lib/ui';

/** Landing page for the emailed verification link: /verify-email?email=...&token=... */
export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get('token');
  // The link has always carried `email` as well — buildVerificationUrl() in the
  // registration service puts both in it. This page read only the token and
  // sent only the token, so the API answered "email and token are required"
  // (HTTP 400) for every verification attempt. The email is not redundant: the
  // service stores only a hash of the token, so it fetches the account by email
  // and then compares.
  const email = params.get('email');
  const [state, setState] = useState(email && token ? 'verifying' : 'missing');
  const [error, setError] = useState(null);
  // Prefilled from the link when present, so the resend form below is one click
  // rather than a retyped address.
  const [resendEmail, setResendEmail] = useState(email || '');
  const [resendBusy, setResendBusy] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (!email || !token) return;
    let cancelled = false;
    registrationApi
      .verifyEmail(email, token)
      .then(() => !cancelled && setState('verified'))
      .catch((err) => {
        if (!cancelled) {
          setError(err);
          setState('failed');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [email, token]);

  async function onResend(event) {
    event.preventDefault();
    setResendBusy(true);
    try {
      await registrationApi.resendVerification(resendEmail);
      toast.success('Verification email sent.');
    } catch (err) {
      toast.error(err);
    } finally {
      setResendBusy(false);
    }
  }

  return (
    <div className={ui.authScreen}>
      <div className={ui.authCard}>
        <h1 className={ui.h1}>Email verification</h1>
        {state === 'missing' && (
          <p>
            This link is incomplete — it needs both the address and the token from your
            verification email. Use the link in that email, or request a new one below.
          </p>
        )}
        {state === 'verifying' && <Spinner label="Verifying your email…" />}
        {state === 'verified' && (
          <>
            <div className={ui.alertSuccess}>Your email is verified.</div>
            <Link className={ui.btnPrimary} to="/login">
              Sign in
            </Link>
          </>
        )}
        {(state === 'failed' || state === 'missing') && (
          <>
            {state === 'failed' && <ErrorAlert error={error} />}
            <p className={cn(ui.muted, 'text-body-s')}>
              Enter your email to get a new verification link.
            </p>
            <form onSubmit={onResend}>
              <Field
                label="Email"
                type="email"
                autoComplete="email"
                required
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
              />
              <button
                className={cn(ui.btnSecondary, 'w-full')}
                type="submit"
                disabled={resendBusy || !resendEmail}
              >
                {resendBusy ? 'Sending…' : 'Resend verification email'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
